import React, { useState, useEffect } from 'react';
import { User, Star, Flame, Award, Shield, BookOpen, MessageSquare, Edit2, Save, LogOut, Bell, Mail, HelpCircle, MessageCircle, AlertCircle, Heart, ChevronRight, Lock, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { formatLevel } from '@/lib/levels';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import api from '@/lib/api';

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/users/profile');
      setProfile(res.data);
      setEditName(res.data.name || '');
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      await api.put('/users/profile', { name: editName });
      await loadProfile();
      await refreshUser();
      setEditing(false);
    } catch (err) { console.error(err) }
  };

  const handleRequestLevel4 = async () => {
    try {
      await api.post('/users/request-level4');
      await loadProfile();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin" />
    </div>
  );

  const xp = profile?.xp_points || 0;
  const currentLevelXp = profile?.current_level_xp || 0;
  const nextLevelXp = profile?.next_level_xp || 100;
  const xpProgress = nextLevelXp > currentLevelXp ? ((xp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100 : 100;

  const levelColors = {
    1: 'from-[#E7E5E4] to-[#D6D3D1]',
    2: 'from-[#EAF1EA] to-[#588157]',
    3: 'from-[#E8F5E9] to-[#4CAF50]',
    4: 'from-[#FDF6F0] to-[#D4A373]'
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-safe">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sm:p-8 mb-6" data-testid="profile-header">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E8F5E9] to-[#4CAF50] flex items-center justify-center text-2xl font-bold text-white flex-shrink-0">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="w-full h-full rounded-2xl object-cover" />
            ) : (
              profile?.name?.charAt(0) || '?'
            )}
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="flex gap-2 mb-2">
                <input value={editName} onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 rounded-xl border-0 py-2 px-3 text-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                  data-testid="edit-name-input"
                />
                <Button size="sm" onClick={handleSave} className="bg-[#588157] hover:bg-[#436442] text-white rounded-xl" data-testid="save-name-button">
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1C1917] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{profile?.name}</h1>
                <button onClick={() => setEditing(true)} className="text-[#A8A29E] hover:text-[#57534E]" data-testid="edit-profile-button">
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
            <p className="text-sm text-[#57534E]">{profile?.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${levelColors[profile?.level || 1]}`}>
                <Shield className="w-3 h-3 mr-1" /> {formatLevel(profile?.level || 1, 'combined')}
              </span>
            </div>
          </div>
        </div>

        {/* XP Progress */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#1C1917]">{formatLevel(profile?.level, 'short')} Progress</span>
            <span className="text-xs text-[#A8A29E]">{xp} / {nextLevelXp} XP</span>
          </div>
          <Progress value={Math.min(xpProgress, 100)} className="h-3 bg-[#F5F5F4]" data-testid="xp-progress" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <ProfileStat icon={<Star className="w-4 h-4 text-[#4CAF50]" />} label="XP" value={xp} testId="profile-xp" />
        <ProfileStat icon={<Flame className="w-4 h-4 text-[#EA580C]" />} label="Streak" value={`${profile?.streak_count || 0}d`} testId="profile-streak" />
        <ProfileStat icon={<BookOpen className="w-4 h-4 text-[#588157]" />} label="Translations" value={profile?.translations_count || 0} testId="profile-translations" />
        <ProfileStat icon={<MessageSquare className="w-4 h-4 text-[#D4A373]" />} label="Posts" value={profile?.posts_count || 0} testId="profile-posts" />
      </div>

      {/* Wirfon (Elder) Request */}
      {profile?.level === 3 && profile?.level4_status === 'none' && (
        <div className="bg-[#FDF6F0] border border-[#D4A373]/30 rounded-2xl p-5 mb-6" data-testid="level4-request-card">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-[#D4A373]" />
            <h3 className="text-sm font-semibold text-[#1C1917]">Ready for Wirfon (Elder) Status?</h3>
          </div>
          <p className="text-xs text-[#57534E] mb-3">As a Wirfon, you can validate translations, answer questions with higher authority, and contribute to language preservation.</p>
          <Button onClick={handleRequestLevel4}
            className="bg-[#D4A373] hover:bg-[#B88A5E] text-white rounded-xl text-sm"
            data-testid="request-level4-button"
          >
            Request Wirfon Status
          </Button>
        </div>
      )}
      {profile?.level4_status === 'pending' && (
        <div className="bg-[#FFF7ED] border border-[#EA580C]/20 rounded-2xl p-5 mb-6" data-testid="level4-pending">
          <p className="text-sm text-[#EA580C] font-medium">Your Wirfon request is pending approval.</p>
        </div>
      )}

      {/* Request Level Upgrade — available to all non-Wirfon users */}
      {profile?.level < 4 && profile?.level4_status !== 'pending' && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 mb-6" data-testid="request-upgrade">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-[#D4A373]" />
            <h3 className="text-sm font-semibold text-[#1C1917]">Request Level Upgrade</h3>
          </div>
          <p className="text-xs text-[#57534E] mb-3">
            {profile?.level < 3
              ? "If you're a fluent Lamnso' speaker, you can request to be upgraded. Admin approval required for Wirfon status."
              : "Ready for Wirfon (Elder) status? Your reviews will carry 3x the weight and help validate translations."}
          </p>
          <div className="flex gap-2">
            {profile?.level < 3 && (
              <Button size="sm" onClick={async () => {
                try {
                  await api.put('/users/profile', { request_level: 3 });
                  await loadProfile();
                } catch (err) { console.error(err); }
              }} className="bg-[#D4A373] hover:bg-[#B88A5E] text-white rounded-xl text-xs" data-testid="request-storyteller-btn">
                Request Ránsísí (Storyteller)
              </Button>
            )}
            <Button size="sm" onClick={handleRequestLevel4}
              className="bg-[#D4A373] hover:bg-[#B88A5E] text-white rounded-xl text-xs" data-testid="request-wirfon-btn">
              Request Wirfon (Elder)
            </Button>
          </div>
        </div>
      )}

      {/* Achievements */}
      {profile?.achievements?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 sm:p-6 mb-6" data-testid="profile-achievements">
          <h2 className="text-lg font-semibold text-[#1C1917] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Earned Achievements
          </h2>
          <div className="flex flex-wrap gap-2">
            {profile.achievements.map(a => (
              <span key={a.achievement_id} className="bg-[#FDF6F0] border border-[#D4A373]/30 text-[#D4A373] px-3 py-1.5 rounded-xl text-xs font-semibold">
                {a.achievement_id.replace('ach_', '').replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Change Password */}
      {profile?.auth_provider === 'email' && <ChangePasswordSection />}

      {/* Notification Settings */}
      <NotificationSettings />

      {/* Help & Support */}
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 mb-6" data-testid="help-support">
        <h2 className="text-sm font-semibold text-[#1C1917] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Help & Support
        </h2>
        <div className="space-y-2.5">
          <a href={`mailto:gills.fai@lamfon.app?subject=${encodeURIComponent(`[LámFon Bug Report] Issue on ${window.location.pathname}`)}&body=${encodeURIComponent(`Hi LámFon team,\n\nI found an issue:\n\nPage: ${window.location.href}\nBrowser: ${navigator.userAgent}\nUser: ${profile?.name || ''} (${profile?.email || ''})\n\nDescription:\n\n\nSteps to reproduce:\n1. \n2. \n3. \n\nExpected behavior:\n\nActual behavior:\n\n`)}`}
            className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-[#E8F5E9] transition-colors border border-[#4CAF50]/20"
            data-testid="report-problem-link">
            <div className="w-8 h-8 rounded-xl bg-[#4CAF50] flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#4CAF50]">Report a Problem</p>
              <p className="text-[10px] text-[#A8A29E]">Pre-filled with your page & browser info</p>
            </div>
          </a>
          <button onClick={() => {
              try {
                const data = JSON.parse(localStorage.getItem('lamfon_walkthrough_completed') || '{}');
                if (profile?.user_id) delete data[profile.user_id];
                localStorage.setItem('lamfon_walkthrough_completed', JSON.stringify(data));
              } catch (e) { console.error('Tour reset error:', e) }
              window.location.href = '/';
            }}
            className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-[#F5F5F4] transition-colors w-full text-left"
            data-testid="take-tour-link">
            <div className="w-8 h-8 rounded-xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
              <PlayCircle className="w-4 h-4 text-[#4CAF50]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1C1917]">Take a Tour</p>
              <p className="text-[10px] text-[#A8A29E]">Guided walkthrough of the app</p>
            </div>
          </button>
          <a href="mailto:gills.fai@lamfon.app"
            className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-[#F5F5F4] transition-colors"
            data-testid="support-email-link">
            <div className="w-8 h-8 rounded-xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-[#4CAF50]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1C1917]">Email Support</p>
              <p className="text-[10px] text-[#A8A29E]">gills.fai@lamfon.app</p>
            </div>
          </a>
          <a href="https://wa.me/17634616615" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-[#F5F5F4] transition-colors"
            data-testid="support-whatsapp-link">
            <div className="w-8 h-8 rounded-xl bg-[#EAF1EA] flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-4 h-4 text-[#588157]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1C1917]">WhatsApp</p>
              <p className="text-[10px] text-[#A8A29E]">Quick questions & feedback</p>
            </div>
          </a>
          <a href="https://lamfonbooks.com" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-[#F5F5F4] transition-colors"
            data-testid="support-books-link">
            <div className="w-8 h-8 rounded-xl bg-[#FDF6F0] flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-4 h-4 text-[#D4A373]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1C1917]">LamFon Books</p>
              <p className="text-[10px] text-[#A8A29E]">Children's books for learning Lamnso'</p>
            </div>
          </a>
        </div>
      </div>

      {/* Support the Project */}
      <div className="bg-gradient-to-br from-[#FDF6F0] to-[#E8F5E9] rounded-2xl border border-[#D4A373]/20 p-5 mb-6" data-testid="support-project">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-5 h-5 text-[#4CAF50]" />
          <h2 className="text-sm font-semibold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Support the Project
          </h2>
        </div>
        <p className="text-xs text-[#57534E] leading-relaxed mb-4">
          LámFon is a community project dedicated to preserving the Lamnso' language. Your support funds development, content creation, and helps keep Lamnso' alive for future generations.
        </p>

        <div className="space-y-2.5">
          {/* Zelle */}
          <div className="bg-white rounded-xl border border-[#E7E5E4] p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-[#6D28D9]">Zelle</span>
              <span className="text-[10px] text-[#A8A29E]">Instant bank transfer</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-xs text-[#57534E] mb-1">Send to: <strong className="text-[#1C1917]">FAI CONSULTING LLC</strong></p>
                <p className="text-xs text-[#57534E]">Zelle #: <strong className="text-[#1C1917]">7634616615</strong></p>
              </div>
              <img src="https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/eqbu2cnn_Image-1.png"
                alt="Zelle QR Code" className="w-20 h-20 rounded-lg border border-[#E7E5E4]" />
            </div>
          </div>

          {/* Buy Books */}
          <a href="https://lamfonbooks.com/?utm_source=lamfon.app&utm_medium=support&utm_campaign=profile"
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white rounded-xl border border-[#E7E5E4] p-4 hover:shadow-sm transition-shadow"
            data-testid="support-books-link">
            <div className="w-10 h-10 rounded-xl bg-[#FDF6F0] flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-[#D4A373]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#1C1917]">Buy LamFon Books</p>
              <p className="text-[10px] text-[#57534E]">Children's books that support the project</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#A8A29E]" />
          </a>

          {/* WhatsApp for other support */}
          <a href="https://wa.me/17634616615?text=Hi%2C%20I%27d%20like%20to%20support%20the%20LamFon%20project"
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white rounded-xl border border-[#E7E5E4] p-4 hover:shadow-sm transition-shadow"
            data-testid="support-whatsapp-link">
            <div className="w-10 h-10 rounded-xl bg-[#EAF1EA] flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5 text-[#588157]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#1C1917]">Other Ways to Support</p>
              <p className="text-[10px] text-[#57534E]">Contact us on WhatsApp</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#A8A29E]" />
          </a>
        </div>
      </div>

      {/* Logout */}
      <button onClick={logout}
        className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-[#4CAF50] hover:text-[#3D8B40] transition-colors"
        data-testid="logout-button"
      >
        <LogOut className="w-4 h-4" /> Sign Out
      </button>
    </div>
  );
}

function NotificationSettings() {
  const { isSupported, isSubscribed, subscribe, unsubscribe } = usePushNotifications();
  const [digestFreq, setDigestFreq] = useState('weekly');
  const [loadingPush, setLoadingPush] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);

  useEffect(() => {
    api.get('/push/email-preferences').then(r => {
      setDigestFreq(r.data.digest_frequency || 'weekly');
    }).catch(err => console.error(err));
  }, []);

  const togglePush = async () => {
    setLoadingPush(true);
    if (isSubscribed) await unsubscribe();
    else await subscribe();
    setLoadingPush(false);
  };

  const updateDigest = async (freq) => {
    setLoadingEmail(true);
    setDigestFreq(freq);
    await api.post('/push/email-preferences', { digest_frequency: freq }).catch(err => console.error(err));
    setLoadingEmail(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 mb-6" data-testid="notification-settings">
      <h2 className="text-sm font-semibold text-[#1C1917] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Notification Settings
      </h2>

      {/* Push Notifications */}
      {isSupported && (
        <div className="flex items-center justify-between py-3 border-b border-[#E7E5E4]">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#57534E]" />
            <div>
              <p className="text-sm text-[#1C1917]">Push Notifications</p>
              <p className="text-[10px] text-[#A8A29E]">Get notified about approvals, replies, and milestones</p>
            </div>
          </div>
          <button onClick={togglePush} disabled={loadingPush}
            className={`relative w-11 h-6 rounded-full transition-colors ${isSubscribed ? 'bg-[#588157]' : 'bg-[#E7E5E4]'}`}
            data-testid="push-toggle">
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isSubscribed ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </div>
      )}

      {/* Email Digest */}
      <div className="py-3">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-4 h-4 text-[#57534E]" />
          <div>
            <p className="text-sm text-[#1C1917]">Email Digest</p>
            <p className="text-[10px] text-[#A8A29E]">Summary of your activity and unread notifications</p>
          </div>
        </div>
        <div className="flex gap-2">
          {[
            { key: 'daily', label: 'Daily' },
            { key: 'weekly', label: 'Weekly' },
            { key: 'none', label: 'Off' },
          ].map(opt => (
            <button key={opt.key} onClick={() => updateDigest(opt.key)} disabled={loadingEmail}
              className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-colors ${
                digestFreq === opt.key ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
              }`}
              data-testid={`digest-${opt.key}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProfileStat({ icon, label, value, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 text-center" data-testid={testId}>
      <div className="flex justify-center mb-1">{icon}</div>
      <p className="text-xl font-bold text-[#1C1917]">{value}</p>
      <p className="text-xs text-[#A8A29E]">{label}</p>
    </div>
  );
}

function ChangePasswordSection() {
  const [showForm, setShowForm] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    setMessage('');
    if (newPw.length < 6) { setError('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await api.post('/auth/change-password', { current_password: currentPw, new_password: newPw });
      setMessage(res.data.message);
      setShowForm(false);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 mb-6" data-testid="change-password-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#57534E]" />
          <h2 className="text-sm font-semibold text-[#1C1917]">Password</h2>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="text-xs font-semibold text-[#4CAF50]" data-testid="change-password-btn">
            Change Password
          </button>
        )}
      </div>

      {message && <p className="text-xs text-[#588157] mt-2 bg-[#EAF1EA] p-2 rounded-lg">{message}</p>}
      {error && <p className="text-xs text-[#4CAF50] mt-2 bg-[#E8F5E9] p-2 rounded-lg">{error}</p>}

      {showForm && (
        <div className="mt-3 space-y-2.5">
          <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
            placeholder="Current password"
            className="w-full rounded-xl border-0 py-2.5 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
            data-testid="current-password-input" />
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password (min 6 characters)"
            className="w-full rounded-xl border-0 py-2.5 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
            data-testid="new-password-input" />
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            placeholder="Confirm new password"
            className="w-full rounded-xl border-0 py-2.5 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
            data-testid="confirm-password-input" />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSubmit} disabled={saving || !currentPw || !newPw || !confirmPw}
              className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs" data-testid="save-password-btn">
              {saving ? 'Saving...' : 'Update Password'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setError(''); }} className="rounded-xl text-xs">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

